import Pusher from 'pusher-js';
import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import rootStore from 'stores';
import { deviceTimezone, timezoneStore } from 'stores/Timezone';
import Logger from 'utils/logger';
import { SessionErrorCode, errorCode, isUnauthorized } from 'utils/api/errors';
import { refreshSession, setSessionRefreshedListener } from 'utils/api/sessionRefresh';
import { env } from 'config';
import {
  BACKGROUND_REQUEST_HEADER,
  BACKGROUND_REQUEST_VALUE,
  DEVICE_TIMEZONE_HEADER,
  READER_TIMEZONE_HEADER,
} from 'core/constants';

declare module 'axios' {
  // oxlint-disable-next-line typescript/no-explicit-any
  interface InternalAxiosRequestConfig<D = any> {
    timezoneSequence?: number;
    sessionRetried?: boolean;
  }
}

export function initLogging(): void {
  if (env.LOGGING_ENABLED !== 'true') {
    return;
  }

  Pusher.log = (msg) => {
    Logger.info(msg);
  };
}

export function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

const withTrailingSlash = (url: string): string => (url.endsWith('/') ? url : `${url}/`);

// Resolved URL, not baseURL: some calls pass an absolute admin-api URL under core-api's baseURL.
export function isCoreApiRequest({ baseURL, url }: Pick<InternalAxiosRequestConfig, 'baseURL' | 'url'>): boolean {
  const uri = axios.getUri({ baseURL, url });
  if (!env.REACT_APP_API_URL || !uri) return false;
  const resolved = withTrailingSlash(uri);
  const coreApi = withTrailingSlash(env.REACT_APP_API_URL);
  if (!resolved.startsWith(coreApi)) return false;
  return ![env.REACT_APP_ADMIN_API_URL, env.REACT_APP_WORKFLOWS_API_URL].some((otherBase) => {
    if (!otherBase) return false;
    const other = withTrailingSlash(otherBase);
    return resolved.startsWith(other) && other.length >= coreApi.length;
  });
}

function applyReaderTimezone(response: AxiosResponse<unknown> | undefined): void {
  const zone = response?.headers?.[READER_TIMEZONE_HEADER];
  const sequence = response?.config?.timezoneSequence;
  if (typeof zone === 'string' && sequence !== undefined) {
    timezoneStore.applyReaderTimezone(zone, sequence);
  }
}

function endSession(): void {
  if (rootStore.session.user) {
    Logger.info('Session expired. Automatically logging out.');
    rootStore.session.logout();
  }
}

const isBackground = (config: InternalAxiosRequestConfig): boolean =>
  config.headers.get(BACKGROUND_REQUEST_HEADER) === BACKGROUND_REQUEST_VALUE;

const isRefreshCall = ({ baseURL, url }: InternalAxiosRequestConfig): boolean =>
  axios.getUri({ baseURL, url }) ===
  axios.getUri({ baseURL: env.REACT_APP_ADMIN_API_URL ?? env.REACT_APP_API_URL, url: '/session/refresh/' });

export function initAxiosInterceptors(): void {
  setSessionRefreshedListener(() => rootStore.session.resubscribeWebsocketChannels());

  // Request interceptor to manually add CSRF token
  // In Axios 1.x, xsrfCookieName/xsrfHeaderName only work for same-origin requests
  axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const csrfCookieName = env.REACT_APP_CSRF_COOKIE_NAME || 'csrftoken';
    const csrfToken = getCookie(csrfCookieName);

    if (csrfToken && config.headers) {
      config.headers['X-CSRFToken'] = csrfToken;
    }

    return config;
  });

  axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    if (isCoreApiRequest(config)) {
      config.timezoneSequence = timezoneStore.nextSequence();
      const zone = deviceTimezone();
      if (zone) {
        config.headers[DEVICE_TIMEZONE_HEADER] = zone;
      }
    }

    return config;
  });

  axios.interceptors.response.use(
    (response: AxiosResponse<unknown>) => {
      applyReaderTimezone(response);
      return response;
    },
    async (error) => {
      applyReaderTimezone(error?.response);
      const config: InternalAxiosRequestConfig | undefined = error?.config;
      const code = isUnauthorized(error) ? errorCode(error) : undefined;

      if (code === SessionErrorCode.RefreshRequired) {
        if (!config || isBackground(config) || config.sessionRetried || isRefreshCall(config)) {
          return Promise.reject(error);
        }
        try {
          await refreshSession();
        } catch {
          return Promise.reject(error);
        }
        config.sessionRetried = true;
        return axios.request(config);
      }

      if (code === SessionErrorCode.Expired) {
        endSession();
      } else if (isUnauthorized(error) && !code && rootStore.session.user) {
        endSession();
      }
      return Promise.reject(error);
    }
  );
}
